import { OAuth2Config, OAuth2Token, UserToken, AuthorizationState } from '../types';

import { ProviderType } from '../providers/provider.factory';
import { OAuth2ProviderFactory } from '../providers/provider.factory';
import { StorageAdapter, CreateUserInput, ProfileBasedTokenOptions } from '../storage/interfaces';
import { InMemoryStorageAdapter } from '../storage/memory.adapter';
import { ProviderFactory } from '../providers/provider.factory';
import { System, Scope, User } from '../types';
import { createCodeVerifier, createCodeChallenge, generateState } from '../utils/crypto';
import { OAuth2Provider } from '../providers/base.provider';
import { UserProfile } from '../providers/interfaces/profile-fetcher.interface';
import { OAuth2GranularOperations, OAuth2GranularClient } from './oauth2-granular.facade';

export interface OAuth2Options {
  storage?: StorageAdapter;
  sealKey?: string;
  providers?: Record<string, OAuth2Config>;
}

export interface AuthorizationOptions {
  provider: string;
  scopes?: string[];
  metadata?: Record<string, any>;
  usePKCE?: boolean;
  userId?: string; // Optional explicit user ID for backend APIs
}

export interface TokenOptions {
  autoRefresh?: boolean;
  refreshBuffer?: number; // minutes before expiry to refresh
  expirationBuffer?: number; // seconds before expiry to consider expired
  defaultExpiresIn?: number; // default expiration in seconds
}

export interface UserCreationOptions {
  email?: string;
  externalId?: string;
  metadata?: Record<string, any>;
}

export interface CallbackOptions {
  userId?: string;
  scopeId?: string;
  profileOptions?: ProfileBasedTokenOptions;
}

export interface CallbackResult {
  userToken: UserToken;
  userId: string;
  systemId: string;
  scopeId: string;
  provider: string;
  profile?: UserProfile; // Available if profile options were used
}

// Main Facade class for better developer experience with context management
export class OAuth2Client {
  private storage: StorageAdapter;
  private providerFactory: ProviderFactory;
  private providers: Map<string, OAuth2Provider> = new Map();
  private providerConfigs: Map<string, OAuth2Config> = new Map();
  private now: () => number;

  // Current context (for context-managed operations)
  private currentSystem?: System;
  private currentUser?: User;
  private defaultScope?: Scope;

  // Granular operations (delegate to separate instance)
  public readonly granular: OAuth2GranularOperations;

  constructor(options: OAuth2Options = {}) {
    this.storage = options.storage || new InMemoryStorageAdapter();
    this.providerFactory = new OAuth2ProviderFactory();
    this.now = Date.now;

    // Create granular operations instance
    this.granular = new OAuth2GranularClient(this.storage, this.providers, this.now);

    // Register predefined providers
    if (options.providers) {
      Object.entries(options.providers).forEach(([name, config]) => {
        this.registerProvider(name, config);
      });
    }
  }

  // ===== Simple Setup Methods =====

  /**
   * Quick setup for common use cases
   */
  static async quickSetup(
    appName: string,
    providers: Record<string, OAuth2Config>,
  ): Promise<OAuth2Client> {
    const client = new OAuth2Client({ providers });

    // Create default system and scope
    const system = await client.createSystem(appName);
    await client.useSystem(system.id);

    const scope = await client.createScope('default', {
      type: 'authentication',
      permissions: ['*'],
      isolated: false,
    });

    client.setDefaultScope(scope.id);

    return client;
  }

  /**
   * Register a provider configuration
   */
  registerProvider(name: string, config: OAuth2Config): void {
    this.providerConfigs.set(name, config);

    // Determine provider type
    const providerType = this.detectProviderType(name, config);
    const provider = this.providerFactory.createProvider(providerType, config);
    this.providers.set(name, provider);
  }

  /**
   * Create or select a system to work with
   */
  async createSystem(name: string, description?: string): Promise<System> {
    const system = await this.storage.createSystem({ name, description, scopes: [] });
    this.currentSystem = system;
    return system;
  }

  async useSystem(systemId: string): Promise<void> {
    const system = await this.storage.getSystem(systemId);
    if (!system) throw new Error('System not found');
    this.currentSystem = system;
  }

  /**
   * Create a scope within the current system
   */
  async createScope(
    name: string,
    options: {
      type?: 'authentication' | 'access' | 'custom';
      permissions?: string[];
      isolated?: boolean;
    } = {},
  ): Promise<Scope> {
    if (!this.currentSystem) throw new Error('No system selected');

    return this.storage.createScope({
      systemId: this.currentSystem.id,
      name,
      type: options.type || 'authentication',
      permissions: options.permissions || [],
      isolated: options.isolated ?? true,
    });
  }

  setDefaultScope(scopeId: string): void {
    this.defaultScope = { id: scopeId } as Scope;
  }

  /**
   * Create a user (legacy method - always creates new user)
   * @deprecated Use getOrCreateUser for better user management
   */
  async createUser(metadata?: Record<string, any>): Promise<User> {
    if (!this.currentSystem) throw new Error('No system selected');

    const user = await this.storage.createUser({
      systemId: this.currentSystem.id,
      metadata,
    });
    this.currentUser = user;
    return user;
  }

  /**
   * Get or create a user (recommended method)
   */
  async getOrCreateUser(options: UserCreationOptions = {}): Promise<User> {
    if (!this.currentSystem) throw new Error('No system selected');

    const input: CreateUserInput = {
      systemId: this.currentSystem.id,
      email: options.email,
      externalId: options.externalId,
      metadata: options.metadata,
    };

    const user = await this.storage.getOrCreateUser(input);
    this.currentUser = user;
    return user;
  }

  /**
   * Get or create a user (stateless version for backend APIs)
   */
  async getOrCreateUserStateless(
    systemId: string,
    options: UserCreationOptions = {},
  ): Promise<User> {
    const input: CreateUserInput = {
      systemId,
      email: options.email,
      externalId: options.externalId,
      metadata: options.metadata,
    };

    return this.storage.getOrCreateUser(input);
  }

  /**
   * Start authorization flow for a specific user (stateless backend API method)
   */
  async authorizeForUser(
    userId: string,
    provider: string,
    options: {
      systemId?: string;
      scopeId?: string;
      scopes?: string[];
      metadata?: Record<string, any>;
      usePKCE?: boolean;
    } = {},
  ): Promise<{ url: string; state: string }> {
    const systemId = options.systemId || this.currentSystem?.id;
    const scopeId = options.scopeId || this.defaultScope?.id;

    if (!systemId) throw new Error('System ID must be provided or current system must be set');

    return this.authorize({
      provider,
      scopes: options.scopes,
      metadata: {
        ...options.metadata,
        systemId,
        scopeId,
      },
      usePKCE: options.usePKCE,
      userId, // Pass the explicit user ID
    });
  }

  /**
   * Complete workflow: get/create user and start authorization (for backend APIs)
   */
  async createUserAndAuthorize(
    systemId: string,
    provider: string,
    userOptions: UserCreationOptions,
    authOptions: {
      scopeId?: string;
      scopes?: string[];
      metadata?: Record<string, any>;
      usePKCE?: boolean;
    } = {},
  ): Promise<{ user: User; authUrl: string; state: string }> {
    // Create or get the user
    const user = await this.getOrCreateUserStateless(systemId, userOptions);

    // Start authorization for this user
    const { url, state } = await this.authorizeForUser(user.id, provider, {
      systemId,
      ...authOptions,
    });

    return {
      user,
      authUrl: url,
      state,
    };
  }

  /**
   * Find user by email
   */
  async findUserByEmail(email: string): Promise<User | null> {
    if (!this.currentSystem) throw new Error('No system selected');
    return this.storage.findUserByEmail(this.currentSystem.id, email);
  }

  /**
   * Find user by email (stateless version)
   */
  async findUserByEmailStateless(systemId: string, email: string): Promise<User | null> {
    return this.storage.findUserByEmail(systemId, email);
  }

  /**
   * Find user by external ID
   */
  async findUserByExternalId(externalId: string): Promise<User | null> {
    if (!this.currentSystem) throw new Error('No system selected');
    return this.storage.findUserByExternalId(this.currentSystem.id, externalId);
  }

  /**
   * Find user by external ID (stateless version)
   */
  async findUserByExternalIdStateless(systemId: string, externalId: string): Promise<User | null> {
    return this.storage.findUserByExternalId(systemId, externalId);
  }

  async useUser(userId: string): Promise<void> {
    const user = await this.storage.getUser(userId);
    if (!user) throw new Error('User not found');
    this.currentUser = user;
  }

  // ===== OAuth Flow Methods =====

  /**
   * Start the OAuth authorization flow
   */
  async authorize(options: AuthorizationOptions): Promise<{ url: string; state: string }> {
    const config = this.providerConfigs.get(options.provider);
    if (!config) throw new Error(`Provider ${options.provider} not registered`);

    const finalConfig: OAuth2Config = {
      ...config,
      scopes: options.scopes || config.scopes,
      usePKCE: options.usePKCE ?? config.usePKCE ?? config.pkce,
    };

    const state = generateState();
    let codeVerifier: string | undefined;
    let codeChallenge: string | undefined;

    if (finalConfig.usePKCE || finalConfig.pkce) {
      codeVerifier = createCodeVerifier();
      codeChallenge = createCodeChallenge(codeVerifier as string);
    }

    // Use explicit userId if provided, otherwise fall back to current user
    const userId = options.userId || this.currentUser?.id;

    const authState: AuthorizationState = {
      state,
      codeVerifier,
      config: finalConfig,
      timestamp: new Date(),
      metadata: {
        ...options.metadata,
        provider: options.provider,
        userId: userId, // Use explicit or current user
        systemId: this.currentSystem?.id,
        scopeId: this.defaultScope?.id,
      },
    };

    await this.storage.saveAuthorizationState(authState);

    const provider = this.providers.get(options.provider);
    if (!provider) throw new Error(`Provider ${options.provider} not found`);

    const url = provider.generateAuthorizationUrl(state, codeChallenge);

    return { url, state };
  }

  /**
   * Handle the OAuth callback
   */
  async handleCallback(
    code: string,
    state: string,
    options?: CallbackOptions,
  ): Promise<CallbackResult> {
    const authState = await this.storage.getAuthorizationState(state);
    if (!authState) throw new Error('Invalid or expired state');

    const metadata = authState.metadata || {};
    const provider = metadata.provider as string;

    console.log('metadata [', metadata, ']');

    if (!provider) throw new Error('Provider not found in state');

    const userId = options?.userId || metadata.userId || this.currentUser?.id;

    console.log('userId [', userId, ']');

    const systemId = metadata.systemId || this.currentSystem?.id;
    const scopeId = options?.scopeId || metadata.scopeId || this.defaultScope?.id;

    if (!userId || !systemId || !scopeId) {
      throw new Error('Missing required context: userId, systemId, or scopeId');
    }

    const providerInstance = this.providers.get(provider);
    if (!providerInstance) throw new Error(`Provider ${provider} not found`);

    const token = await providerInstance.exchangeCodeForToken(code, authState.codeVerifier);

    // Clean up the used state
    await this.storage.deleteAuthorizationState(state);

    let userToken: UserToken;
    let profile: UserProfile | undefined;

    // Always use email-based validation if provider supports profile fetching

    console.log(
      'providerInstance.hasProfileFetcher() [',
      providerInstance.hasProfileFetcher(),
      ']',
    );

    if (providerInstance.hasProfileFetcher()) {
      try {
        // Fetch user profile to get the email
        profile = await providerInstance.fetchProfile(token.accessToken);

        console.log('profile [', profile, ']');

        // Always use email validation to prevent duplicate emails for same provider
        userToken = await this.storage.saveTokenWithEmailValidation(
          userId,
          systemId,
          scopeId,
          provider,
          profile.email,
          {
            userId,
            systemId,
            scopeId,
            provider,
            token,
          },
        );

        // Apply additional profile options if provided
        if (options?.profileOptions?.mergeUserData) {
          await this.mergeUserDataFromProfile(userId, profile);
        }
      } catch (error) {
        console.warn('Failed to fetch profile or save with email validation:', error);
        // Fallback to saving without email validation
        userToken = await this.storage.saveToken({
          userId,
          systemId,
          scopeId,
          provider,
          token,
        });
      }
    } else {
      // Provider doesn't support profile fetching - save token normally
      userToken = await this.storage.saveToken({
        userId,
        systemId,
        scopeId,
        provider,
        token,
      });
    }

    return {
      userToken,
      userId,
      systemId,
      scopeId,
      provider,
      profile,
    };
  }

  /**
   * Merge user data from OAuth profile
   */
  private async mergeUserDataFromProfile(userId: string, profile: UserProfile): Promise<void> {
    const user = await this.storage.getUser(userId);
    if (!user) return;

    const updatedMetadata = {
      ...user.metadata,
      email: profile.email,
      name: profile.name || user.metadata?.name,
      avatar: profile.avatar || user.metadata?.avatar,
      username: profile.username || user.metadata?.username,
      profileId: profile.id || user.metadata?.profileId,
      lastProfileUpdate: new Date().toISOString(),
    };

    await this.storage.updateUser(userId, { metadata: updatedMetadata });
  }

  /**
   * Fetch user profile for a given provider and user
   * Note: If user has multiple tokens for the provider, this will fail.
   * Use fetchUserProfileByEmail() for unambiguous profile fetching.
   */
  async fetchUserProfile(provider: string, userId?: string): Promise<UserProfile> {
    if (!userId && !this.currentUser) {
      throw new Error('No user specified and no current user set');
    }

    const actualUserId = userId || this.currentUser!.id;
    const systemId = this.currentSystem?.id;
    const scopeId = this.defaultScope?.id;

    if (!systemId || !scopeId) {
      throw new Error('Missing system or scope context');
    }

    const userTokens = await this.storage.getTokensByUserScopeProvider(
      actualUserId,
      scopeId,
      provider,
    );
    if (userTokens.length === 0) {
      throw new Error('No token found for user and provider');
    }

    // If multiple tokens exist, this is ambiguous
    if (userTokens.length > 1) {
      throw new Error(
        `Multiple tokens found for provider ${provider}. Use fetchUserProfileByEmail() to specify which token to use.`,
      );
    }

    const providerInstance = this.providers.get(provider);
    if (!providerInstance) throw new Error(`Provider ${provider} not found`);

    // Use the single token
    const userToken = userTokens[0];
    if (!userToken) {
      throw new Error('Token not found');
    }

    const accessToken = userToken.token.accessToken;

    return providerInstance.fetchProfile(accessToken);
  }

  /**
   * Fetch user profile by email (unambiguous)
   */
  async fetchUserProfileByEmail(
    provider: string,
    email: string,
    systemId?: string,
    scopeId?: string,
  ): Promise<UserProfile> {
    const actualSystemId = systemId || this.currentSystem?.id;
    const actualScopeId = scopeId || this.defaultScope?.id;

    if (!actualSystemId) {
      throw new Error('System ID must be provided or current system must be set');
    }
    if (!actualScopeId) {
      throw new Error('Scope ID must be provided or default scope must be set');
    }

    const accessToken = await this.getAccessTokenByEmail(
      email,
      provider,
      actualSystemId,
      actualScopeId,
    );

    const providerInstance = this.providers.get(provider);
    if (!providerInstance) throw new Error(`Provider ${provider} not found`);

    return providerInstance.fetchProfile(accessToken);
  }

  /**
   * Replace tokens for users with conflicting email addresses
   */
  async replaceConflictingTokensByEmail(
    email: string,
    provider: string,
    newUserId: string,
    newToken: OAuth2Token,
  ): Promise<UserToken> {
    if (!this.currentSystem || !this.defaultScope) {
      throw new Error('Missing system or scope context');
    }

    return this.storage.replaceTokensByEmailInUserScopeProvider(
      newUserId,
      this.defaultScope.id,
      provider,
      email,
      {
        userId: newUserId,
        systemId: this.currentSystem.id,
        scopeId: this.defaultScope.id,
        provider,
        token: newToken,
      },
    );
  }

  /**
   * Check if a token is expired
   */
  isTokenExpired(token: OAuth2Token, options: TokenOptions = {}): boolean {
    const { expirationBuffer = 300 } = options;

    // Use createdAt if available, otherwise use expiresAt
    if (token.createdAt && token.expiresIn !== undefined) {
      const expiresAt = token.createdAt + token.expiresIn * 1000;
      const effectiveNow = this.now() + expirationBuffer * 1000;
      return effectiveNow >= expiresAt;
    }

    // Fallback to expiresAt date
    const expiresAt = new Date(token.expiresAt).getTime();
    const effectiveNow = this.now() + expirationBuffer * 1000;
    return effectiveNow >= expiresAt;
  }

  /**
   * Get a valid access token (auto-refresh if needed)
   * Uses current context (user + default scope)
   */
  async getAccessToken(provider: string, options: TokenOptions = {}): Promise<string> {
    if (!this.currentUser || !this.currentSystem || !this.defaultScope) {
      throw new Error('Missing required context');
    }

    const userTokens = await this.storage.getTokensByUserScopeProvider(
      this.currentUser.id,
      this.defaultScope.id,
      provider,
    );

    if (userTokens.length === 0) {
      throw new Error('No token found for provider');
    }

    // If multiple tokens exist, this is ambiguous - user should use email-specific methods
    if (userTokens.length > 1) {
      throw new Error(
        `Multiple tokens found for provider ${provider}. Use getAccessTokenByEmail() or getAllValidTokensForUser() to specify which token to use.`,
      );
    }

    const userToken = userTokens[0];
    if (!userToken) {
      throw new Error('No token found for provider');
    }

    // Check if token needs refresh
    const needsRefresh =
      options.autoRefresh !== false && this.isTokenExpired(userToken.token, options);

    if (!needsRefresh) {
      return userToken.token.accessToken;
    }

    // Refresh the token
    if (!userToken.token.refreshToken) {
      throw new Error('Token expired and no refresh token available');
    }

    const providerInstance = this.providers.get(provider);
    if (!providerInstance) throw new Error(`Provider ${provider} not found`);

    const newToken = await providerInstance.refreshToken(userToken.token.refreshToken);

    await this.storage.updateToken(userToken.id, { token: newToken });

    return newToken.accessToken;
  }

  /**
   * Get access token by email (unambiguous)
   */
  async getAccessTokenByEmail(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
    options: TokenOptions = {},
  ): Promise<string> {
    const token = await this.getValidTokenForEmail(email, provider, systemId, scopeId, options);
    return token.accessToken;
  }

  /**
   * Ensure we have a valid token, refreshing if needed
   * Uses current context (user + default scope)
   */
  async ensureValidToken(provider: string, options: TokenOptions = {}): Promise<OAuth2Token> {
    if (!this.currentUser || !this.currentSystem || !this.defaultScope) {
      throw new Error('Missing required context');
    }

    const userTokens = await this.storage.getTokensByUserScopeProvider(
      this.currentUser.id,
      this.defaultScope.id,
      provider,
    );

    if (userTokens.length === 0) {
      throw new Error(`No tokens found for provider ${provider}`);
    }

    // If multiple tokens exist, this is ambiguous - user should use email-specific methods
    if (userTokens.length > 1) {
      throw new Error(
        `Multiple tokens found for provider ${provider}. Use getValidTokenForEmail() to specify which token to use.`,
      );
    }

    const userToken = userTokens[0];
    if (!userToken) {
      throw new Error(`No tokens found for provider ${provider}`);
    }

    if (!this.isTokenExpired(userToken.token, options)) {
      return userToken.token;
    }

    // Token is expired, refresh it
    if (!userToken.token.refreshToken) {
      throw new Error('Token expired and no refresh token available');
    }

    const providerInstance = this.providers.get(provider);
    if (!providerInstance) throw new Error(`Provider ${provider} not found`);

    const newToken = await providerInstance.refreshToken(userToken.token.refreshToken);
    await this.storage.updateToken(userToken.id, { token: newToken });

    return newToken;
  }

  /**
   * Execute a callback with a valid access token
   * Uses current context (user + default scope)
   */
  async withValidToken<T>(
    provider: string,
    callback: (accessToken: string) => Promise<T>,
    options: TokenOptions = {},
  ): Promise<T> {
    const token = await this.ensureValidToken(provider, options);
    return await callback(token.accessToken);
  }

  /**
   * Get user token entity (includes all metadata) for specific user
   */
  async getUserTokenForUser(
    userId: string,
    _systemId: string,
    scopeId: string,
    provider: string,
  ): Promise<UserToken | null> {
    const userTokens = await this.storage.getTokensByUserScopeProvider(userId, scopeId, provider);
    return userTokens.length > 0 && userTokens[0] ? userTokens[0] : null;
  }

  /**
   * Check if token exists for specific user/provider combination
   */
  async hasTokenForUser(
    userId: string,
    _systemId: string,
    scopeId: string,
    provider: string,
  ): Promise<boolean> {
    const userTokens = await this.storage.getTokensByUserScopeProvider(userId, scopeId, provider);
    return userTokens.length > 0;
  }

  /**
   * Revoke tokens for a specific user and provider (stateless method)
   * This removes ALL tokens for the user/scope/provider combination
   */
  async revokeTokensForUser(
    userId: string,
    _systemId: string,
    scopeId: string,
    provider: string,
  ): Promise<void> {
    const userTokens = await this.storage.getTokensByUserScopeProvider(userId, scopeId, provider);
    for (const userToken of userTokens) {
      await this.storage.deleteToken(userToken.id);
    }
  }

  /**
   * Revoke tokens for a provider
   */
  async revokeTokens(provider: string): Promise<void> {
    if (!this.currentUser || !this.currentSystem || !this.defaultScope) {
      throw new Error('Missing required context');
    }

    const userTokens = await this.storage.getTokensByUserScopeProvider(
      this.currentUser.id,
      this.defaultScope.id,
      provider,
    );

    for (const userToken of userTokens) {
      await this.storage.deleteToken(userToken.id);
    }
  }

  /**
   * Get all tokens for the current user
   */
  async getUserTokens(): Promise<UserToken[]> {
    if (!this.currentUser) throw new Error('No user selected');
    return this.storage.getTokensByUser(this.currentUser.id);
  }

  /**
   * Get user information by internal user ID
   * Returns user data including external ID (in systemId field)
   */
  async getUserById(userId: string): Promise<User | null> {
    const user = await this.storage.getUser(userId);
    if (!user) {
      return null;
    }

    // User object already contains:
    // - id: internal user ID
    // - systemId: external user ID
    // - metadata: additional user data (may also contain externalId)
    return user;
  }

  /**
   * Get user by internal ID with explicit external ID field
   * Returns user with both internal ID and external ID clearly labeled
   */
  async getUserByInternalId(
    userId: string,
  ): Promise<{ id: string; externalId: string; user: User } | null> {
    const user = await this.storage.getUser(userId);
    if (!user) {
      return null;
    }

    return {
      id: user.id, // Internal ID
      externalId: user.metadata?.externalId || user.systemId, // External ID
      user, // Full user entity
    };
  }

  /**
   * Get user information by external ID
   * Returns the full user entity including both internal ID and external ID
   */
  async getUserByExternalId(systemId: string, externalId: string): Promise<User | null> {
    return this.storage.findUserByExternalId(systemId, externalId);
  }

  /**
   * Get tokens for a user by internal user ID with user profile
   * Includes external ID in the response
   */
  async getTokensByInternalUserId(
    userId: string,
    options?: { includeProfile?: boolean },
  ): Promise<Array<UserToken & { user?: User }>> {
    const user = await this.storage.getUser(userId);
    if (!user) {
      throw new Error(`User with ID ${userId} not found`);
    }

    const tokens = await this.storage.getTokensByUser(userId);

    if (options?.includeProfile) {
      return tokens.map((token) => ({
        ...token,
        user: {
          id: user.id,
          systemId: user.systemId,
          metadata: user.metadata,
          createdAt: user.createdAt,
          updatedAt: user.updatedAt,
        },
      }));
    }

    return tokens;
  }

  /**
   * Get tokens for a user by internal user ID for a specific provider
   * Includes external ID in the response
   */
  async getTokensByInternalUserIdAndProvider(
    userId: string,
    provider: string,
  ): Promise<Array<UserToken & { externalId?: string }>> {
    const user = await this.storage.getUser(userId);
    if (!user) {
      throw new Error(`User with ID ${userId} not found`);
    }

    const tokens = await this.storage.getTokensByUserAndProvider(userId, provider);

    // Add external ID from user metadata if available
    const externalId = user.metadata?.externalId || user.systemId;

    return tokens.map((token) => ({
      ...token,
      externalId,
    }));
  }

  /**
   * Get tokens for a user by internal user ID for a specific scope
   * Includes external ID in the response
   */
  async getTokensByInternalUserIdAndScope(
    userId: string,
    scopeId: string,
  ): Promise<Array<UserToken & { externalId?: string }>> {
    const user = await this.storage.getUser(userId);
    if (!user) {
      throw new Error(`User with ID ${userId} not found`);
    }

    const tokens = await this.storage.getTokensByUserAndScope(userId, scopeId);

    // Add external ID from user metadata if available
    const externalId = user.metadata?.externalId || user.systemId;

    return tokens.map((token) => ({
      ...token,
      externalId,
    }));
  }

  /**
   * Get a specific token by internal user ID, scope, and provider
   * Includes full user information including external ID
   */
  async getTokenByInternalUserId(
    userId: string,
    scopeId: string,
    provider: string,
  ): Promise<(UserToken & { user: User }) | null> {
    const user = await this.storage.getUser(userId);
    if (!user) {
      throw new Error(`User with ID ${userId} not found`);
    }

    const tokens = await this.storage.getTokensByUserScopeProvider(userId, scopeId, provider);

    if (tokens.length === 0 || !tokens[0]) {
      return null;
    }

    const token = tokens[0];
    return {
      ...token,
      user,
    };
  }

  /**
   * Get all valid tokens for a user by internal ID with auto-refresh
   * Includes external ID in the response
   */
  async getAllValidTokensByInternalUserId(
    userId: string,
    options: TokenOptions = {},
  ): Promise<
    Array<{
      provider: string;
      scopeId: string;
      token: OAuth2Token;
      userToken: UserToken;
      externalId?: string;
    }>
  > {
    const user = await this.storage.getUser(userId);
    if (!user) {
      throw new Error(`User with ID ${userId} not found`);
    }

    const externalId = user.metadata?.externalId || user.systemId;
    const userTokens = await this.storage.getTokensByUser(userId);
    const validTokens: Array<{
      provider: string;
      scopeId: string;
      token: OAuth2Token;
      userToken: UserToken;
      externalId?: string;
    }> = [];

    for (const userToken of userTokens) {
      if (!userToken) continue;

      try {
        // Check if token needs refresh
        const needsRefresh =
          options.autoRefresh !== false && this.isTokenExpired(userToken.token, options);

        let finalToken = userToken.token;

        if (needsRefresh) {
          // Try to refresh the token
          if (!userToken.token.refreshToken) {
            console.warn(
              `Token for provider ${userToken.provider} expired and no refresh token available`,
            );
            continue;
          }

          const providerInstance = this.providers.get(userToken.provider);
          if (!providerInstance) {
            console.warn(`Provider ${userToken.provider} not found`);
            continue;
          }

          try {
            finalToken = await providerInstance.refreshToken(userToken.token.refreshToken);
            await this.storage.updateToken(userToken.id, { token: finalToken });
          } catch (error) {
            console.warn(`Failed to refresh token for provider ${userToken.provider}:`, error);
            continue;
          }
        }

        validTokens.push({
          provider: userToken.provider,
          scopeId: userToken.scopeId,
          token: finalToken,
          userToken: { ...userToken, token: finalToken },
          externalId,
        });
      } catch (error) {
        console.warn(`Error processing token for provider ${userToken.provider}:`, error);
      }
    }

    return validTokens;
  }

  /**
   * Get all tokens for a user by ID with validation and auto-refresh
   * @deprecated Use getAllValidTokensByInternalUserId instead
   */
  async getAllValidTokensForUser(
    userId: string,
    options: TokenOptions = {},
  ): Promise<{ provider: string; scopeId: string; token: OAuth2Token; userToken: UserToken }[]> {
    const userTokens = await this.storage.getTokensByUser(userId);
    const validTokens: {
      provider: string;
      scopeId: string;
      token: OAuth2Token;
      userToken: UserToken;
    }[] = [];

    for (const userToken of userTokens) {
      if (!userToken) continue;

      try {
        // Check if token needs refresh
        const needsRefresh =
          options.autoRefresh !== false && this.isTokenExpired(userToken.token, options);

        let finalToken = userToken.token;

        if (needsRefresh) {
          // Try to refresh the token
          if (!userToken.token.refreshToken) {
            console.warn(
              `Token for provider ${userToken.provider} expired and no refresh token available`,
            );
            continue; // Skip this token
          }

          const providerInstance = this.providers.get(userToken.provider);
          if (!providerInstance) {
            console.warn(`Provider ${userToken.provider} not found`);
            continue; // Skip this token
          }

          try {
            const newToken = await providerInstance.refreshToken(userToken.token.refreshToken);
            await this.storage.updateToken(userToken.id, { token: newToken });
            finalToken = newToken;
          } catch (refreshError) {
            console.warn(
              `Failed to refresh token for provider ${userToken.provider}:`,
              refreshError,
            );
            continue; // Skip this token
          }
        }

        validTokens.push({
          provider: userToken.provider,
          scopeId: userToken.scopeId,
          token: finalToken,
          userToken: { ...userToken, token: finalToken },
        });
      } catch (error) {
        console.warn(`Error processing token for provider ${userToken.provider}:`, error);
        continue; // Skip this token
      }
    }

    return validTokens;
  }

  /**
   * Get all valid tokens for a user by email with validation and auto-refresh
   */
  async getAllValidTokensForEmail(
    email: string,
    systemId?: string,
    options: TokenOptions = {},
  ): Promise<{ provider: string; scopeId: string; token: OAuth2Token; userToken: UserToken }[]> {
    const actualSystemId = systemId || this.currentSystem?.id;
    if (!actualSystemId) {
      throw new Error('System ID must be provided or current system must be set');
    }

    // Get all tokens for this email
    const userTokens = await this.storage.findTokensByEmail(email, actualSystemId);
    const validTokens: {
      provider: string;
      scopeId: string;
      token: OAuth2Token;
      userToken: UserToken;
    }[] = [];

    for (const userToken of userTokens) {
      if (!userToken) continue;

      try {
        // Check if token needs refresh
        const needsRefresh =
          options.autoRefresh !== false && this.isTokenExpired(userToken.token, options);

        let finalToken = userToken.token;

        if (needsRefresh) {
          // Try to refresh the token
          if (!userToken.token.refreshToken) {
            console.warn(
              `Token for provider ${userToken.provider} expired and no refresh token available`,
            );
            continue; // Skip this token
          }

          const providerInstance = this.providers.get(userToken.provider);
          if (!providerInstance) {
            console.warn(`Provider ${userToken.provider} not found`);
            continue; // Skip this token
          }

          try {
            const newToken = await providerInstance.refreshToken(userToken.token.refreshToken);
            await this.storage.updateToken(userToken.id, { token: newToken });
            finalToken = newToken;
          } catch (refreshError) {
            console.warn(
              `Failed to refresh token for provider ${userToken.provider}:`,
              refreshError,
            );
            continue; // Skip this token
          }
        }

        validTokens.push({
          provider: userToken.provider,
          scopeId: userToken.scopeId,
          token: finalToken,
          userToken: { ...userToken, token: finalToken },
        });
      } catch (error) {
        console.warn(`Error processing token for provider ${userToken.provider}:`, error);
        continue; // Skip this token
      }
    }

    return validTokens;
  }

  /**
   * Get specific token for an email
   */
  async getTokenForEmail(
    email: string,
    provider: string,
    systemId?: string,
    scopeId?: string,
  ): Promise<UserToken | null> {
    const actualSystemId = systemId || this.currentSystem?.id;
    const actualScopeId = scopeId || this.defaultScope?.id;

    if (!actualSystemId) {
      throw new Error('System ID must be provided or current system must be set');
    }
    if (!actualScopeId) {
      throw new Error('Scope ID must be provided or default scope must be set');
    }

    return this.storage.findTokenByEmailScopeProvider(
      email,
      actualSystemId,
      actualScopeId,
      provider,
    );
  }

  /**
   * Get valid token for an email (with auto-refresh)
   */
  async getValidTokenForEmail(
    email: string,
    provider: string,
    systemId?: string,
    scopeId?: string,
    options: TokenOptions = {},
  ): Promise<OAuth2Token> {
    const actualSystemId = systemId || this.currentSystem?.id;
    const actualScopeId = scopeId || this.defaultScope?.id;

    if (!actualSystemId) {
      throw new Error('System ID must be provided or current system must be set');
    }
    if (!actualScopeId) {
      throw new Error('Scope ID must be provided or default scope must be set');
    }

    const userToken = await this.storage.findTokenByEmailScopeProvider(
      email,
      actualSystemId,
      actualScopeId,
      provider,
    );

    if (!userToken) {
      throw new Error(`No token found for email ${email} and provider ${provider}`);
    }

    // Check if token needs refresh
    const needsRefresh =
      options.autoRefresh !== false && this.isTokenExpired(userToken.token, options);

    if (!needsRefresh) {
      return userToken.token;
    }

    // Refresh the token
    if (!userToken.token.refreshToken) {
      throw new Error('Token expired and no refresh token available');
    }

    const providerInstance = this.providers.get(provider);
    if (!providerInstance) throw new Error(`Provider ${provider} not found`);

    const newToken = await providerInstance.refreshToken(userToken.token.refreshToken);
    await this.storage.updateToken(userToken.id, { token: newToken });

    return newToken;
  }

  /**
   * Get valid access token for an email (with auto-refresh)
   */
  async getAccessTokenForEmail(
    email: string,
    provider: string,
    systemId?: string,
    scopeId?: string,
    options: TokenOptions = {},
  ): Promise<string> {
    const token = await this.getValidTokenForEmail(email, provider, systemId, scopeId, options);
    return token.accessToken;
  }

  /**
   * Execute a callback with a valid access token for an email
   */
  async withValidTokenForEmail<T>(
    email: string,
    provider: string,
    callback: (accessToken: string) => Promise<T>,
    systemId?: string,
    scopeId?: string,
    options: TokenOptions = {},
  ): Promise<T> {
    const token = await this.getValidTokenForEmail(email, provider, systemId, scopeId, options);
    return await callback(token.accessToken);
  }

  /**
   * Check if token exists for specific email/provider combination
   */
  async hasTokenForEmail(
    email: string,
    provider: string,
    systemId?: string,
    scopeId?: string,
  ): Promise<boolean> {
    const actualSystemId = systemId || this.currentSystem?.id;
    const actualScopeId = scopeId || this.defaultScope?.id;

    if (!actualSystemId) {
      throw new Error('System ID must be provided or current system must be set');
    }
    if (!actualScopeId) {
      throw new Error('Scope ID must be provided or default scope must be set');
    }

    const token = await this.storage.findTokenByEmailScopeProvider(
      email,
      actualSystemId,
      actualScopeId,
      provider,
    );
    return token !== null;
  }

  /**
   * Revoke tokens for a specific email and provider
   */
  async revokeTokensForEmail(
    email: string,
    provider: string,
    systemId?: string,
    scopeId?: string,
  ): Promise<void> {
    const actualSystemId = systemId || this.currentSystem?.id;
    const actualScopeId = scopeId || this.defaultScope?.id;

    if (!actualSystemId) {
      throw new Error('System ID must be provided or current system must be set');
    }
    if (!actualScopeId) {
      throw new Error('Scope ID must be provided or default scope must be set');
    }

    const token = await this.storage.findTokenByEmailScopeProvider(
      email,
      actualSystemId,
      actualScopeId,
      provider,
    );
    if (token) {
      await this.storage.deleteToken(token.id);
    }
  }

  /**
   * Get tokens by scope (stateless method)
   */
  async getTokensByScope(systemId?: string, scopeId?: string): Promise<UserToken[]> {
    const actualSystemId = systemId || this.currentSystem?.id;
    const actualScopeId = scopeId || this.defaultScope?.id;

    if (!actualSystemId) {
      throw new Error('System ID must be provided or current system must be set');
    }
    if (!actualScopeId) {
      throw new Error('Scope ID must be provided or default scope must be set');
    }

    return this.storage.getTokensByScope(actualSystemId, actualScopeId);
  }

  /**
   * Find token by email and scope
   */
  async findTokenByEmailAndScope(
    email: string,
    provider: string,
    systemId?: string,
    scopeId?: string,
  ): Promise<UserToken | null> {
    const actualSystemId = systemId || this.currentSystem?.id;
    const actualScopeId = scopeId || this.defaultScope?.id;

    if (!actualSystemId) {
      throw new Error('System ID must be provided or current system must be set');
    }
    if (!actualScopeId) {
      throw new Error('Scope ID must be provided or default scope must be set');
    }

    return this.storage.findTokenByEmailScopeProvider(
      email,
      actualSystemId,
      actualScopeId,
      provider,
    );
  }

  /**
   * Find all tokens by email and scope
   */
  async findAllTokensByEmailAndScope(
    email: string,
    provider: string,
    systemId?: string,
    scopeId?: string,
  ): Promise<UserToken[]> {
    const actualSystemId = systemId || this.currentSystem?.id;
    const actualScopeId = scopeId || this.defaultScope?.id;

    if (!actualSystemId) {
      throw new Error('System ID must be provided or current system must be set');
    }
    if (!actualScopeId) {
      throw new Error('Scope ID must be provided or default scope must be set');
    }

    return this.storage
      .findTokensByEmailAndScope(email, actualSystemId, actualScopeId)
      .then((tokens) => tokens.filter((t) => t.provider === provider));
  }

  // ===== Utility Methods =====

  private detectProviderType(name: string, config: OAuth2Config): ProviderType {
    // Check by name first
    const knownProviders: ProviderType[] = ['google', 'github', 'microsoft', 'outlook', 'facebook'];
    if (knownProviders.includes(name as ProviderType)) {
      return name as ProviderType;
    }

    // Check by URL patterns
    if (config.authorizationUrl.includes('google.com')) return 'google';
    if (config.authorizationUrl.includes('github.com')) return 'github';
    if (
      config.authorizationUrl.includes('microsoft.com') ||
      config.authorizationUrl.includes('microsoftonline.com')
    )
      return 'microsoft';
    if (config.authorizationUrl.includes('facebook.com')) return 'facebook';

    return 'generic';
  }

  /**
   * Clean up expired authorization states
   */
  async cleanup(expiryMs: number = 10 * 60 * 1000): Promise<void> {
    await this.storage.cleanupExpiredStates(expiryMs);
  }

  /**
   * Get all valid tokens for a user with OAuth provider profile information (RECOMMENDED FOR UI)
   * This fetches the actual email/profile from each OAuth provider using the access token
   */
  async getAllValidTokensWithProviderProfiles(
    userId: string,
    options: TokenOptions = {},
  ): Promise<
    {
      provider: string;
      scopeId: string;
      token: OAuth2Token;
      userToken: UserToken;
      profile?: UserProfile;
    }[]
  > {
    const userTokens = await this.storage.getTokensByUser(userId);
    const validTokens: {
      provider: string;
      scopeId: string;
      token: OAuth2Token;
      userToken: UserToken;
      profile?: UserProfile;
    }[] = [];

    for (const userToken of userTokens) {
      if (!userToken) continue;

      try {
        // Check if token needs refresh
        const needsRefresh =
          options.autoRefresh !== false && this.isTokenExpired(userToken.token, options);

        let finalToken = userToken.token;

        if (needsRefresh) {
          // Try to refresh the token
          if (!userToken.token.refreshToken) {
            console.warn(
              `Token for provider ${userToken.provider} expired and no refresh token available`,
            );
            continue; // Skip this token
          }

          const providerInstance = this.providers.get(userToken.provider);
          if (!providerInstance) {
            console.warn(`Provider ${userToken.provider} not found`);
            continue; // Skip this token
          }

          try {
            const newToken = await providerInstance.refreshToken(userToken.token.refreshToken);
            await this.storage.updateToken(userToken.id, { token: newToken });
            finalToken = newToken;
          } catch (refreshError) {
            console.warn(
              `Failed to refresh token for provider ${userToken.provider}:`,
              refreshError,
            );
            continue; // Skip this token
          }
        }

        // Fetch OAuth provider profile if provider supports it
        let profile: UserProfile | undefined;
        const providerInstance = this.providers.get(userToken.provider);
        if (providerInstance && providerInstance.hasProfileFetcher()) {
          try {
            profile = await providerInstance.fetchProfile(finalToken.accessToken);
          } catch (profileError) {
            console.warn(
              `Failed to fetch profile for provider ${userToken.provider}:`,
              profileError,
            );
            // Continue without profile rather than skipping the token
          }
        }

        validTokens.push({
          provider: userToken.provider,
          scopeId: userToken.scopeId,
          token: finalToken,
          userToken: { ...userToken, token: finalToken },
          profile,
        });
      } catch (error) {
        console.warn(`Error processing token for provider ${userToken.provider}:`, error);
        continue; // Skip this token
      }
    }

    return validTokens;
  }

  /**
   * Get all valid tokens for a user and scope with OAuth provider profile information
   */
  async getAllValidTokensForUserAndScopeWithProviderProfiles(
    userId: string,
    scopeId: string,
    options: TokenOptions = {},
  ): Promise<
    { provider: string; token: OAuth2Token; userToken: UserToken; profile?: UserProfile }[]
  > {
    const userTokens = await this.storage.getTokensByUserAndScope(userId, scopeId);
    const validTokens: {
      provider: string;
      token: OAuth2Token;
      userToken: UserToken;
      profile?: UserProfile;
    }[] = [];

    for (const userToken of userTokens) {
      if (!userToken) continue;

      try {
        // Check if token needs refresh
        const needsRefresh =
          options.autoRefresh !== false && this.isTokenExpired(userToken.token, options);

        let finalToken = userToken.token;

        if (needsRefresh) {
          // Try to refresh the token
          if (!userToken.token.refreshToken) {
            console.warn(
              `Token for provider ${userToken.provider} expired and no refresh token available`,
            );
            continue; // Skip this token
          }

          const providerInstance = this.providers.get(userToken.provider);
          if (!providerInstance) {
            console.warn(`Provider ${userToken.provider} not found`);
            continue; // Skip this token
          }

          try {
            const newToken = await providerInstance.refreshToken(userToken.token.refreshToken);
            await this.storage.updateToken(userToken.id, { token: newToken });
            finalToken = newToken;
          } catch (refreshError) {
            console.warn(
              `Failed to refresh token for provider ${userToken.provider}:`,
              refreshError,
            );
            continue; // Skip this token
          }
        }

        // Fetch OAuth provider profile if provider supports it
        let profile: UserProfile | undefined;
        const providerInstance = this.providers.get(userToken.provider);
        if (providerInstance && providerInstance.hasProfileFetcher()) {
          try {
            profile = await providerInstance.fetchProfile(finalToken.accessToken);
          } catch (profileError) {
            console.warn(
              `Failed to fetch profile for provider ${userToken.provider}:`,
              profileError,
            );
            // Continue without profile rather than skipping the token
          }
        }

        validTokens.push({
          provider: userToken.provider,
          token: finalToken,
          userToken: { ...userToken, token: finalToken },
          profile,
        });
      } catch (error) {
        console.warn(`Error processing token for provider ${userToken.provider}:`, error);
        continue; // Skip this token
      }
    }

    return validTokens;
  }

  /**
   * Get all valid tokens for a user and provider with OAuth provider profile information
   */
  async getAllValidTokensForUserAndProviderWithProviderProfiles(
    userId: string,
    provider: string,
    options: TokenOptions = {},
  ): Promise<
    { scopeId: string; token: OAuth2Token; userToken: UserToken; profile?: UserProfile }[]
  > {
    const userTokens = await this.storage.getTokensByUserAndProvider(userId, provider);
    const validTokens: {
      scopeId: string;
      token: OAuth2Token;
      userToken: UserToken;
      profile?: UserProfile;
    }[] = [];

    for (const userToken of userTokens) {
      if (!userToken) continue;

      try {
        // Check if token needs refresh
        const needsRefresh =
          options.autoRefresh !== false && this.isTokenExpired(userToken.token, options);

        let finalToken = userToken.token;

        if (needsRefresh) {
          // Try to refresh the token
          if (!userToken.token.refreshToken) {
            console.warn(
              `Token for provider ${userToken.provider} expired and no refresh token available`,
            );
            continue; // Skip this token
          }

          const providerInstance = this.providers.get(userToken.provider);
          if (!providerInstance) {
            console.warn(`Provider ${userToken.provider} not found`);
            continue; // Skip this token
          }

          try {
            const newToken = await providerInstance.refreshToken(userToken.token.refreshToken);
            await this.storage.updateToken(userToken.id, { token: newToken });
            finalToken = newToken;
          } catch (refreshError) {
            console.warn(
              `Failed to refresh token for provider ${userToken.provider}:`,
              refreshError,
            );
            continue; // Skip this token
          }
        }

        // Fetch OAuth provider profile if provider supports it
        let profile: UserProfile | undefined;
        const providerInstance = this.providers.get(userToken.provider);
        if (providerInstance && providerInstance.hasProfileFetcher()) {
          try {
            profile = await providerInstance.fetchProfile(finalToken.accessToken);
          } catch (profileError) {
            console.warn(
              `Failed to fetch profile for provider ${userToken.provider}:`,
              profileError,
            );
            // Continue without profile rather than skipping the token
          }
        }

        validTokens.push({
          scopeId: userToken.scopeId,
          token: finalToken,
          userToken: { ...userToken, token: finalToken },
          profile,
        });
      } catch (error) {
        console.warn(`Error processing token for provider ${userToken.provider}:`, error);
        continue; // Skip this token
      }
    }

    return validTokens;
  }

  /**
   * Get OAuth provider profile for a specific token
   * This fetches the actual email/profile from the OAuth provider using the access token
   */
  async getTokenProfile(token: OAuth2Token, provider: string): Promise<UserProfile | null> {
    const providerInstance = this.providers.get(provider);
    if (!providerInstance || !providerInstance.hasProfileFetcher()) {
      return null;
    }

    try {
      return await providerInstance.fetchProfile(token.accessToken);
    } catch (error) {
      console.warn(`Failed to fetch profile for provider ${provider}:`, error);
      return null;
    }
  }

  /**
   * Get OAuth provider profile for a specific user token
   * This fetches the actual email/profile from the OAuth provider using the access token
   */
  async getUserTokenProfile(userToken: UserToken): Promise<UserProfile | null> {
    return this.getTokenProfile(userToken.token, userToken.provider);
  }
}
